import { Component, OnInit, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TripettoToFormlyConverterService } from '../../../../services/tripetto-to-formly-converter/tripetto-to-formly-converter.service';
import { ConversionResult, Definition, DefinitionVersion } from '@nx-ahs/formly/shared';

@Component({
	selector: 'nx-ahs-import-from-tripetto-dialog',
	templateUrl: './import-from-tripetto-dialog.component.html',
	styleUrls: ['./import-from-tripetto-dialog.component.scss'],
	standalone: false
})
export class ImportFromTripettoDialogComponent implements OnInit {
	// Data
	definitions: Definition[] = [];
	filteredDefinitions: Observable<Definition[]>;
	selectedDefinition: Definition | null = null;
	versions: DefinitionVersion[] = [];
	selectedVersion: DefinitionVersion | null = null;

	// Conversion results
	conversionResult: ConversionResult | null = null;
	isConverting = false;
	isLoadingDefinitions = true;
	isLoadingVersions = false;

	// Form controls
	searchControl = new FormControl('');

	constructor(
		public dialogRef: MatDialogRef<ImportFromTripettoDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any,
		private converterService: TripettoToFormlyConverterService
	) {}

	ngOnInit(): void {
		this.loadDefinitions();
		this.setupSearch();
	}

	/**
	 * Load all Tripetto Definitions from the API
	 */
	private loadDefinitions(): void {
		this.isLoadingDefinitions = true;

		this.converterService.getDefinitions().subscribe({
			next: (definitions) => {
				// Filter out deleted definitions
				this.definitions = definitions.filter((d) => !d.isDeleted);
				this.isLoadingDefinitions = false;
			},
			error: (error) => {
				console.error('Failed to load definitions:', error);
				this.isLoadingDefinitions = false;
			}
		});
	}

	/**
	 * Setup search functionality for filtering definitions
	 */
	private setupSearch(): void {
		this.filteredDefinitions = this.searchControl.valueChanges.pipe(
			startWith(''),
			map((value) => this.filterDefinitions(value || ''))
		);
	}

	/**
	 * Filter definitions based on search term
	 */
	private filterDefinitions(searchTerm: string): Definition[] {
		if (!searchTerm) {
			return this.definitions;
		}

		const lowerSearch = searchTerm.toLowerCase();
		return this.definitions.filter(
			(def) =>
				def.name.toLowerCase().includes(lowerSearch) ||
				def.description?.toLowerCase().includes(lowerSearch) ||
				def.definitionId.toString().includes(lowerSearch)
		);
	}

	/**
	 * Handle definition selection
	 */
	public onDefinitionSelected(definition: Definition): void {
		this.selectedDefinition = definition;
		this.selectedVersion = null;
		this.conversionResult = null;
		this.loadVersions(definition.definitionId);
	}

	/**
	 * Load versions for the selected definition
	 */
	private loadVersions(definitionId: number): void {
		this.isLoadingVersions = true;

		this.converterService.getDefinitionVersions(definitionId).subscribe({
			next: (versions) => {
				this.versions = versions.sort((a, b) => {
					// Sort by published status first, then by version ID descending
					if (a.isPublished && !b.isPublished) return -1;
					if (!a.isPublished && b.isPublished) return 1;
					return b.versionId - a.versionId;
				});

				// Auto-select the first version (usually the published or latest one)
				if (this.versions.length > 0) {
					this.onVersionSelected(this.versions[0]);
				}

				this.isLoadingVersions = false;
			},
			error: (error) => {
				console.error('Failed to load versions:', error);
				this.isLoadingVersions = false;
			}
		});
	}

	/**
	 * Handle version selection and trigger conversion
	 */
	public onVersionSelected(version: DefinitionVersion): void {
		this.selectedVersion = version;
		this.runConversion();
	}

	/**
	 * Run the conversion process
	 */
	private runConversion(): void {
		if (!this.selectedVersion || !this.selectedDefinition) {
			return;
		}

		this.isConverting = true;

		try {
			const definitionData = this.selectedVersion.definitionData;

			// Create metadata
			const metadata = {
				definitionId: this.selectedDefinition.definitionId,
				versionId: this.selectedVersion.versionId,
				name: this.selectedDefinition.name,
				version: this.selectedVersion.version
			};

			// Run conversion
			this.conversionResult = this.converterService.convert(definitionData, metadata);
		} catch (error) {
			console.error('Conversion failed:', error);
			// Create an error result
			this.conversionResult = {
				formlyConfig: [],
				warnings: [
					{
						severity: 'error',
						nodeId: '',
						nodeName: 'Conversion',
						blockType: '',
						message: `Conversion failed: ${error.message}`
					}
				],
				statistics: {
					totalNodes: 0,
					convertedNodes: 0,
					skippedNodes: 0,
					nodesWithWarnings: 1,
					complexBranches: 0,
					hiddenFields: 0
				}
			};
		} finally {
			this.isConverting = false;
		}
	}

	/**
	 * Import the converted configuration and close dialog
	 */
	public onImport(): void {
		if (this.conversionResult) {
			this.dialogRef.close(this.conversionResult);
		}
	}

	/**
	 * Cancel and close dialog
	 */
	public onCancel(): void {
		this.dialogRef.close(null);
	}

	/**
	 * Download a detailed conversion report
	 */
	public downloadReport(): void {
		if (!this.conversionResult) {
			return;
		}

		const report = this.generateConversionReport();
		const blob = new Blob([report], { type: 'text/plain' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `tripetto-to-formly-conversion-report-${Date.now()}.txt`;
		link.click();
		window.URL.revokeObjectURL(url);
	}

	/**
	 * Generate a detailed text report of the conversion
	 */
	private generateConversionReport(): string {
		if (!this.conversionResult) {
			return '';
		}

		const { statistics, warnings, formlyConfig, sourceDefinition } = this.conversionResult;

		let report = '================================================\n';
		report += 'TRIPETTO TO FORMLY CONVERSION REPORT\n';
		report += '================================================\n\n';

		if (sourceDefinition) {
			report += 'SOURCE DEFINITION:\n';
			report += `  Name: ${sourceDefinition.name}\n`;
			report += `  Definition ID: ${sourceDefinition.definitionId}\n`;
			report += `  Version ID: ${sourceDefinition.versionId}\n`;
			report += `  Version: ${sourceDefinition.version}\n\n`;
		}

		report += 'CONVERSION STATISTICS:\n';
		report += `  Total Nodes: ${statistics.totalNodes}\n`;
		report += `  Successfully Converted: ${statistics.convertedNodes}\n`;
		report += `  Skipped: ${statistics.skippedNodes}\n`;
		report += `  Nodes with Warnings: ${statistics.nodesWithWarnings}\n`;
		report += `  Complex Branches: ${statistics.complexBranches}\n`;
		report += `  Hidden Fields: ${statistics.hiddenFields}\n\n`;

		if (warnings.length > 0) {
			const errors = warnings.filter((w) => w.severity === 'error');
			const warningsList = warnings.filter((w) => w.severity === 'warning');
			const infos = warnings.filter((w) => w.severity === 'info');

			report += 'WARNINGS AND ISSUES:\n\n';

			if (errors.length > 0) {
				report += `ERRORS (${errors.length}):\n`;
				errors.forEach((warning, index) => {
					report += `  ${index + 1}. [${warning.blockType}] ${warning.nodeName}\n`;
					report += `     ${warning.message}\n`;
					if (warning.suggestion) {
						report += `     Suggestion: ${warning.suggestion}\n`;
					}
					report += '\n';
				});
			}

			if (warningsList.length > 0) {
				report += `WARNINGS (${warningsList.length}):\n`;
				warningsList.forEach((warning, index) => {
					report += `  ${index + 1}. [${warning.blockType}] ${warning.nodeName}\n`;
					report += `     ${warning.message}\n`;
					if (warning.suggestion) {
						report += `     Suggestion: ${warning.suggestion}\n`;
					}
					report += '\n';
				});
			}

			if (infos.length > 0) {
				report += `INFORMATIONAL (${infos.length}):\n`;
				infos.forEach((info, index) => {
					report += `  ${index + 1}. [${info.blockType}] ${info.nodeName}\n`;
					report += `     ${info.message}\n\n`;
				});
			}
		}

		report += 'CONVERTED FORMLY CONFIGURATION:\n';
		report += JSON.stringify(formlyConfig, null, 2);
		report += '\n\n';

		report += '================================================\n';
		report += 'END OF REPORT\n';
		report += '================================================\n';

		return report;
	}

	/**
	 * Check if import button should be enabled
	 */
	public get canImport(): boolean {
		return (
			this.conversionResult !== null &&
			this.conversionResult.formlyConfig.length > 0 &&
			!this.isConverting &&
			!this.isLoadingVersions
		);
	}
}
